import { MusicLyrics } from "@/types/artifact";

// 提取常量
// 支持两种格式：HH:MM:SS.mmm 或 MM:SS.mmm（缺少小时部分）
const SRT_TIMESTAMP_REGEX =
  /(\d{1,2}:\d{2}(?::\d{2})?[,.]\d{3})\s*-->\s*(\d{1,2}:\d{2}(?::\d{2})?[,.]\d{3})/;
const SEQUENCE_NUMBER_REGEX = /^\d+$/;

// 提取类型
type LyricEntry = MusicLyrics[0];

/**
 * 将 SRT 时间戳字符串转换为毫秒数
 * @param timeString 时间戳字符串，格式为：
 *   - 标准格式：HH:MM:SS,mmm 或 HH:MM:SS.mmm
 *   - 简化格式：MM:SS.mmm（缺少小时部分，默认为 00）
 * @returns 毫秒数
 */
export function timestampToMilliseconds(timeString: string): number {
  // 先尝试匹配标准格式 HH:MM:SS.mmm
  const standardMatch = timeString.match(/(\d{2}):(\d{2}):(\d{2})[,.](\d{3})/);
  if (standardMatch) {
    const hours = parseInt(standardMatch[1], 10);
    const minutes = parseInt(standardMatch[2], 10);
    const seconds = parseInt(standardMatch[3], 10);
    const milliseconds = parseInt(standardMatch[4], 10);
    return hours * 3600000 + minutes * 60000 + seconds * 1000 + milliseconds;
  }

  // 尝试匹配简化格式 MM:SS.mmm（缺少小时部分）
  const simplifiedMatch = timeString.match(/(\d{1,2}):(\d{2})[,.](\d{3})/);
  if (simplifiedMatch) {
    const minutes = parseInt(simplifiedMatch[1], 10);
    const seconds = parseInt(simplifiedMatch[2], 10);
    const milliseconds = parseInt(simplifiedMatch[3], 10);
    // 小时部分默认为 0
    return minutes * 60000 + seconds * 1000 + milliseconds;
  }

  return 0;
}

/**
 * 尝试从时间戳行提取开始和结束时间（毫秒）
 */
function extractTimestamps(
  timeLine: string
): { start: number; end: number } | null {
  const match = timeLine.match(SRT_TIMESTAMP_REGEX);
  if (!match) return null;

  return {
    start: timestampToMilliseconds(match[1]),
    end: timestampToMilliseconds(match[2]),
  };
}

/**
 * 解析 SRT 格式的字幕内容为 MusicLyrics 数组
 * @param srtContent SRT 格式的字符串内容
 * @returns MusicLyrics 数组，如果解析失败则返回空数组
 */
export function parseSrtToMusicLyrics(srtContent?: string | null): MusicLyrics {
  if (!srtContent || typeof srtContent !== "string" || !srtContent.trim()) {
    return [];
  }

  // 移除 Markdown 代码块标记（```srt 和 ```）
  let cleanedContent = srtContent.trim();
  cleanedContent = cleanedContent.replace(/^```\w*\n?/i, ""); // 移除开头的 ```srt 或 ```
  cleanedContent = cleanedContent.replace(/\n?```\s*$/i, ""); // 移除结尾的 ```

  const lines = cleanedContent.trim().split("\n");
  const blocks = cleanedContent.trim().split(/\n\s*\n/);

  // 如果只有一个块，说明没有空行分隔，需要按行解析
  return blocks.length === 1 && lines.length > 0
    ? parseSrtByLines(lines)
    : parseSrtByBlocks(blocks);
}

/**
 * 解析按空行分隔的标准 SRT 格式
 */
function parseSrtByBlocks(blocks: string[]): MusicLyrics {
  return blocks
    .map((block) => parseSrtBlock(block.trim().split("\n")))
    .filter((entry): entry is LyricEntry => entry !== null);
}

/**
 * 解析单个 SRT 条目块
 */
function parseSrtBlock(blockLines: string[]): LyricEntry | null {
  // 查找时间戳行
  const timeLineIndex = blockLines.findIndex((line) => line.includes("-->"));
  if (timeLineIndex === -1) return null;

  const timestamps = extractTimestamps(blockLines[timeLineIndex]);
  if (!timestamps) return null;

  // 提取文本内容（时间戳行之后的所有行）
  const text = blockLines
    .slice(timeLineIndex + 1)
    .join(" ")
    .trim();

  return text ? { ...timestamps, text } : null;
}

/**
 * 解析没有空行分隔的 SRT 内容
 */
function parseSrtByLines(lines: string[]): MusicLyrics {
  const lyrics: MusicLyrics = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]?.trim();

    // 跳过空行或非序号行
    if (!line || !SEQUENCE_NUMBER_REGEX.test(line)) {
      i++;
      continue;
    }

    // 解析当前条目
    const entry = parseSrtEntryFromLines(lines, i + 1);
    if (entry) {
      lyrics.push(entry.lyric);
      i = entry.nextIndex;
    } else {
      i++;
    }
  }

  return lyrics;
}

/**
 * 从指定位置开始解析单个 SRT 条目
 * @returns 解析结果和下一个条目的起始索引
 */
function parseSrtEntryFromLines(
  lines: string[],
  startIndex: number
): { lyric: LyricEntry; nextIndex: number } | null {
  if (startIndex >= lines.length) return null;

  // 解析时间戳行
  const timestamps = extractTimestamps(lines[startIndex]);
  if (!timestamps) return null;

  // 收集文本内容，直到遇到下一个序号行或文件结束
  const textLines: string[] = [];
  let i = startIndex + 1;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (SEQUENCE_NUMBER_REGEX.test(line)) break;
    if (line) textLines.push(line);
    i++;
  }

  const text = textLines.join(" ").trim();

  return text ? { lyric: { ...timestamps, text }, nextIndex: i } : null;
}
